import { CAN_PLAY_TYPES, DECODING_PROBES } from '../data/media';
import type { Collector } from './types';
import { safe, signal, unavailable, withTimeout } from './util';

let voicesMemo: Promise<SpeechSynthesisVoice[]> | undefined;

/** Voices load asynchronously in Chromium; wait for `voiceschanged` (bounded), then memoize. */
function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  if (voicesMemo) return voicesMemo;
  const synth = window.speechSynthesis;
  const p = new Promise<SpeechSynthesisVoice[]>((resolve) => {
    const now = synth.getVoices();
    if (now.length) return resolve(now);
    const done = () => {
      synth.removeEventListener('voiceschanged', done);
      clearTimeout(timer);
      resolve(synth.getVoices());
    };
    const timer = setTimeout(done, 1500);
    synth.addEventListener('voiceschanged', done);
  });
  // Only memoize a non-empty list, so a slow first load can still succeed on the second pass.
  voicesMemo = p.then((v) => {
    if (!v.length) voicesMemo = undefined;
    return v;
  });
  return voicesMemo;
}

export const mediaCollector: Collector = {
  id: 'media',
  title: 'Media & speech',
  description: 'Cameras and microphones (counted, never opened), supported codecs, and installed text-to-speech voices.',
  async collect() {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    return Promise.all([
      safe('media.devices', 'Media devices (no permission)', async () => {
        if (!nav?.mediaDevices?.enumerateDevices)
          return unavailable('media.devices', 'Media devices (no permission)', 'enumerateDevices() not available (requires HTTPS)');
        const devices = await withTimeout(nav.mediaDevices.enumerateDevices(), 1500, 'enumerateDevices');
        const counts = { audioinput: 0, audiooutput: 0, videoinput: 0 };
        for (const d of devices) if (d.kind in counts) counts[d.kind as keyof typeof counts]++;
        const labelsVisible = devices.some((d) => d.label !== '');
        return signal('media.devices', 'Media devices (no permission)', { ...counts, labelsVisible }, {
          display: `microphones: ${counts.audioinput} · speakers: ${counts.audiooutput} · cameras: ${counts.videoinput}${labelsVisible ? ' · device names visible' : ''}`,
          note: labelsVisible
            ? 'Device names are visible because this site was granted camera/microphone access before.'
            : 'Without permission, browsers hide names and often report at most one device per kind.',
        });
      }),

      safe('media.codecs', 'Hardware video decoding', async () => {
        const mc = nav?.mediaCapabilities;
        if (!mc?.decodingInfo) return unavailable('media.codecs', 'Hardware video decoding', 'Media Capabilities API not available');
        const entries = await Promise.all(
          Object.entries(DECODING_PROBES).map(async ([name, contentType]) => {
            try {
              const r = await withTimeout(
                mc.decodingInfo({
                  type: 'file',
                  video: { contentType, width: 1920, height: 1080, bitrate: 5_000_000, framerate: 30 },
                }),
                1500,
                'decodingInfo',
              );
              return [name, { supported: r.supported, smooth: r.smooth, powerEfficient: r.powerEfficient }] as const;
            } catch {
              return [name, null] as const;
            }
          }),
        );
        const value = Object.fromEntries(entries);
        const describe = (r: (typeof entries)[number][1]) =>
          !r ? 'error' : !r.supported ? 'no' : `yes${r.smooth ? ', smooth' : ''}${r.powerEfficient ? ', hardware' : ''}`;
        return signal('media.codecs', 'Hardware video decoding', value, {
          display: entries.map(([n, r]) => `${n}: ${describe(r)}`).join(' · '),
        });
      }),

      safe('media.canPlayType', 'Playable formats', () => {
        if (typeof document === 'undefined') return unavailable('media.canPlayType', 'Playable formats', 'No document');
        const audio = document.createElement('audio');
        const video = document.createElement('video');
        if (typeof video.canPlayType !== 'function') return unavailable('media.canPlayType', 'Playable formats', 'canPlayType() not available');
        const value: Record<string, string> = {};
        for (const t of CAN_PLAY_TYPES.audio) value[t] = audio.canPlayType(t);
        for (const t of CAN_PLAY_TYPES.video) value[t] = video.canPlayType(t);
        const by = (answer: string) =>
          Object.entries(value)
            .filter(([, v]) => v === answer)
            .map(([k]) => k);
        return signal('media.canPlayType', 'Playable formats', value, {
          display: `probably (${by('probably').length}): ${by('probably').join(', ') || '-'}\nmaybe (${by('maybe').length}): ${by('maybe').join(', ') || '-'}\nno (${by('').length}): ${by('').join(', ') || '-'}`,
        });
      }),

      safe('media.voices', 'Speech synthesis voices', async () => {
        if (typeof window === 'undefined' || !window.speechSynthesis)
          return unavailable('media.voices', 'Speech synthesis voices', 'Speech synthesis not available');
        const voices = await loadVoices();
        const list = voices.map((v) => `${v.name} (${v.lang})${v.localService ? '' : ' [network]'}${v.default ? ' [default]' : ''}`).sort();
        return signal('media.voices', 'Speech synthesis voices', list, {
          display: list.length ? `${list.length} voices\n${list.join(', ')}` : '0 voices',
          note: 'Listing voices does not use the network, even for voices marked [network].',
        });
      }),
    ]);
  },
};
